import React, { useEffect, useState, useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import { auth } from "../../../config/firebase";
import { useTranslations } from "../../../translations";
import { summarizeEvents } from "./summarizeEvents";

const API_BASE = (
  process.env.REACT_APP_BLOOMS_API_URL || "https://ai-blooms.vercel.app/api/openai/blooms"
).replace(/\/api\/.*/, "");

const LOCALE = { es: "es-ES", en: "en-US", ta: "ta-IN" };
const MODULE_ICON = {
  alphabet: "🔤", sounds: "🔊", write: "✏️", words: "📚", complete: "🧩", family: "👨‍👩‍👧",
  colors: "🎨", math: "🔢", shapes: "🔷", read: "📖", other: "✨",
};

/** Resumen amigable de la actividad de un aprendiz en la app (últimos 90 días). */
const StudentActivity = ({ uid }) => {
  const { t, language } = useTranslations();
  const [events, setEvents] = useState(null); // null = cargando
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!uid) return;
    let active = true;
    setEvents(null);
    setError(null);
    (async () => {
      try {
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        const res = await fetch(`${API_BASE}/api/school/student-events?uid=${encodeURIComponent(uid)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Error");
        if (active) setEvents(data.events || []);
      } catch (e) {
        if (active) setError(e.message);
      }
    })();
    return () => { active = false; };
  }, [uid]);

  const s = useMemo(() => (events ? summarizeEvents(events) : null), [events]);
  const locale = LOCALE[language] || "es-ES";
  const fmtMinutes = (m) => (m < 1 ? "< 1 min" : m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`);
  const fmtDay = (ms) => new Date(ms).toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "short" });
  const modName = (m) => `${MODULE_ICON[m] || ""} ${t(`actMod_${m}`)}`;

  if (error) return <div className="text-danger small py-3">{error}</div>;
  if (!s) return <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>;
  if (!s.activeDays) return <div className="text-muted text-center py-4">{t("actNone")}</div>;

  return (
    <div className="sa-wrap">
      <div className="sa-period">{t("actPeriod")}</div>
      <div className="sa-stats">
        <div className="sa-stat"><div className="sa-num">{new Date(s.lastActivity).toLocaleString(locale, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</div><div className="sa-lbl">{t("actLast")}</div></div>
        <div className="sa-stat"><div className="sa-num">{s.activeDays}</div><div className="sa-lbl">{t("actActiveDays")}</div></div>
        <div className="sa-stat"><div className="sa-num">{fmtMinutes(s.minutes)}</div><div className="sa-lbl">{t("actTime")}</div></div>
        <div className="sa-stat"><div className="sa-num">⭐ {s.stars}</div><div className="sa-lbl">{t("actStars")}{s.starsTotal != null ? ` · ${t("actStarsTotal")} ${s.starsTotal}` : ""}</div></div>
        <div className="sa-stat"><div className="sa-num">🏅 {s.mastered}</div><div className="sa-lbl">{t("actMastered")}</div></div>
      </div>

      {s.modules.length > 0 && (
        <>
          <div className="sa-h">{t("actModules")}</div>
          {s.modules.map(([m, n]) => (
            <div key={m} className="sa-bar-row">
              <span className="sa-bar-name">{modName(m)}</span>
              <span className="sa-bar"><span style={{ width: `${(n / s.modules[0][1]) * 100}%` }} /></span>
              <span className="sa-bar-n">{n}</span>
            </div>
          ))}
        </>
      )}

      {s.highlights.length > 0 && (
        <>
          <div className="sa-h">{t("actHighlights")}</div>
          {s.highlights.map((h, i) => (
            <div key={i} className="sa-hl">
              <span className="sa-hl-date">{fmtDay(h.ts)}</span>
              {h.type === "family"
                ? `👨‍👩‍👧 ${t("actFamilyAdded").replace("{name}", h.name || "")}${h.member ? ` (${h.member})` : ""}`
                : `📝 ${t("actWordAdded").replace("{word}", h.word || "")}`}
            </div>
          ))}
        </>
      )}

      <div className="sa-h">{t("actByDay")}</div>
      {s.days.map((d) => (
        <div key={d.date} className="sa-day">
          <div className="sa-day-date">{fmtDay(d.date)}</div>
          <div className="sa-day-body">
            <span>⏱ {fmtMinutes(d.minutes)}</span>
            {d.modules.length > 0 && <span>{d.modules.map(modName).join(", ")}</span>}
            {d.stars > 0 && <span>⭐ +{d.stars}</span>}
            {d.mastered > 0 && <span>🏅 {d.mastered}</span>}
          </div>
        </div>
      ))}

      <style>{`
        .sa-period { font-size:.8rem; color:#888; margin-bottom:8px; }
        .sa-stats { display:grid; grid-template-columns:repeat(auto-fit,minmax(130px,1fr)); gap:8px; margin-bottom:16px; }
        .sa-stat { background:#f7f7fa; border-radius:12px; padding:10px 12px; }
        .sa-num { font-weight:700; font-size:1.05rem; }
        .sa-lbl { font-size:.75rem; color:#777; }
        .sa-h { font-weight:700; margin:16px 0 8px; }
        .sa-bar-row { display:flex; align-items:center; gap:8px; margin-bottom:6px; font-size:.9rem; }
        .sa-bar-name { flex:0 0 150px; }
        .sa-bar { flex:1; height:8px; background:#eee; border-radius:4px; overflow:hidden; }
        .sa-bar span { display:block; height:100%; background:#0984e3; border-radius:4px; }
        .sa-bar-n { flex:0 0 32px; text-align:right; color:#777; font-size:.8rem; }
        .sa-hl { font-size:.9rem; margin-bottom:4px; }
        .sa-hl-date, .sa-day-date { color:#888; font-size:.8rem; margin-right:8px; }
        .sa-day { display:flex; gap:8px; padding:6px 0; border-bottom:1px solid #f0f0f0; font-size:.9rem; }
        .sa-day-date { flex:0 0 90px; margin-right:0; padding-top:2px; }
        .sa-day-body { display:flex; flex-wrap:wrap; gap:4px 14px; }
      `}</style>
    </div>
  );
};

/** La misma vista dentro de un modal; `student` = { uid, learnerName } o null para cerrarlo. */
export const StudentActivityModal = ({ student, onHide }) => {
  const { t } = useTranslations();
  return (
    <Modal show={!!student} onHide={onHide} centered size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fs-5">📈 {t("actTitle")} {student?.learnerName || ""}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{student && <StudentActivity uid={student.uid} />}</Modal.Body>
    </Modal>
  );
};

export default StudentActivity;
